/* ---------------------------------------------------------------------------
 * ZH MEDIA
 *
 * Drives the video case of snippets/zh-media.liquid, wherever in the theme that
 * slot happens to sit. Four jobs:
 *
 *   1. Pick the file that matches the breakpoint before anything is fetched.
 *   2. Play only while the slot is on screen. The markup carries no autoplay
 *      attribute (it would fetch the file at parse time), so this is also what
 *      starts the video in the first place.
 *   3. Try again when a browser refuses to start the video, for these slots and
 *      for the <video-media> elements Impact brings its own player for.
 *   4. Run the pause control that WCAG 2.2.2 requires for a looping autoplay
 *      video.
 *
 * prefers-reduced-motion is deliberately NOT consulted here. It used to strip the
 * autoplay attribute and hold the slot on its poster, which is the friendlier
 * default but not something WCAG 2.2 AA requires: the binding criterion is 2.2.2
 * Pause, Stop, Hide (Level A), and the pause control below satisfies it on its own.
 * Product decision of 16.09.2026, taken after the setting turned the homepage hero
 * into a black rectangle for every visitor who had it enabled. The page motion layer
 * (assets/zh-home-motion.js, the preloader, the marquees) still honours it; this is
 * about background video only.
 *
 * Everything hangs off [data-zh-media], so a section that renders the snippet
 * needs no JS of its own. Idempotent per element: initialising twice is a no-op,
 * which is what makes the Shopify editor's section reload safe.
 * ------------------------------------------------------------------------- */
(function () {
  'use strict';

  var INITIALISED = 'zhMediaReady';

  function videoOf(scope) {
    return scope.querySelector('video');
  }

  /* The label and the glyph both follow the actual state rather than the click,
     so a video the browser refused to start does not end up labelled "pause". */
  function setToggleState(button, playing) {
    var label = playing ? button.getAttribute('data-label-pause') : button.getAttribute('data-label-play');
    if (label) {
      button.setAttribute('aria-label', label);
      button.setAttribute('title', label);
    }
    /* U+FE0E hinter dem Dreieck erzwingt die Textdarstellung. U+25B6 hat eine
       Emoji-Praesentation, und iOS rendert es ohne den Selector als farbiges
       Emoji statt als Glyphe in der Schriftfarbe des Knopfs. U+2759 (das
       Pausenzeichen) hat keine, braucht also nichts. */
    button.textContent = playing ? '❙❙' : '▶︎';
  }

  /* Source swap.
   *
   * Only for a slot that carries both files. Setting `src` on the element wins
   * over the <source> child that the markup ships, which is what lets the
   * desktop file stay the no-JS default. With preload="none" this runs before
   * any byte is fetched, so exactly one of the two files is ever downloaded.
   *
   * load() is called only on a real change, otherwise a resize inside the same
   * breakpoint would restart playback. */
  /* The mobile file can be one we encoded and uploaded ourselves (see
     snippets/zh-video-web-sources.liquid). Unlike a Shopify rendition it can go
     missing: a deleted file, or an entry in the map that outlived its upload,
     answers 404 and the slot would sit on its poster frame forever with nothing in
     the console to explain it.

     One shot, and only from mobile back to desktop: if the Shopify source fails too
     there is nothing left to try, and the guard on the current src stops the two
     from ping-ponging. */
  function armSourceFallback(scope, video) {
    if (video.zhMediaFallbackArmed) return;
    video.zhMediaFallbackArmed = true;

    video.addEventListener('error', function () {
      var desktop = scope.getAttribute('data-zh-media-desktop');
      var mobile = scope.getAttribute('data-zh-media-mobile');
      if (!desktop || !mobile) return;
      if (video.getAttribute('src') !== mobile) return;

      video.setAttribute('src', desktop);
      video.load();
      play(video);
    });
  }

  function applySource(scope, video) {
    var desktop = scope.getAttribute('data-zh-media-desktop');
    var mobile = scope.getAttribute('data-zh-media-mobile');
    if (!desktop || !mobile) return;

    armSourceFallback(scope, video);

    var breakpoint = parseInt(scope.getAttribute('data-zh-media-breakpoint'), 10) || 699;
    var wanted = window.matchMedia('(max-width: ' + breakpoint + 'px)').matches ? mobile : desktop;

    if (video.getAttribute('src') === wanted) return;
    var wasPlaying = !video.paused;
    video.setAttribute('src', wanted);
    video.load();
    if (wasPlaying) play(video);
  }

  /* Autoplay is blocked often enough that the rejection has to be swallowed:
     an unhandled rejection here would show up as a console error on every page
     that carries a video slot. The poster frame stays visible either way. */
  function play(video) {
    var attempt = video.play();
    if (!attempt || typeof attempt.catch !== 'function') return;

    /* Refusals are routine: iOS blocks autoplay outright in Low Power Mode, and
       several browsers want a gesture first. Swallowed, or every page carrying a
       video slot logs an unhandled rejection. Resynced, because a refused start must
       not leave the control claiming the video is running. */
    attempt.catch(function () {
      syncToggle(video);
      standInFor(video);
    });
  }

  /* The poster stand-in.
   *
   * WebKit draws its own start-playback button, a grey disc with a white triangle,
   * over any video it will not start, and in iOS Low Power Mode it will not start
   * one at all. It lands dead centre of the shot, it belongs to no design of ours,
   * and only the visitors with that setting ever see it.
   *
   * It cannot be styled away. Measured on iOS 26, iPhone 17 Pro: page CSS on
   * ::-webkit-media-controls-start-playback-button does nothing, nor does hiding the
   * bar's own play button, nor hiding ::-webkit-media-controls and its enclosure.
   * The button is drawn regardless.
   *
   * So the video stops being there to decorate. A refused slot gets an <img> with the
   * same poster the <video> was already showing, which is the same picture and leaves
   * WebKit nothing to draw on, and the video is hidden behind it.
   *
   * Hiding it does not stop it: a display:none <video> still plays on a gesture, and
   * currentTime still advances. Measured in the same run, against a WKWebView with
   * mediaTypesRequiringUserActionForPlayback = .all. So the retry above keeps working
   * while the still is up, and the still comes down on the `playing` event, which is
   * the moment there is a real frame to show instead.
   *
   * No poster, no stand-in: there would be nothing to put up, and an empty box is
   * worse than a frame the browser may yet decode. */
  function standInFor(video) {
    if (!video || video.zhMediaStill) return;

    var poster = video.getAttribute('poster');
    if (!poster) return;

    var parent = video.parentNode;
    if (!parent || typeof parent.insertBefore !== 'function') return;

    var still = document.createElement('img');
    still.setAttribute('src', poster);
    still.setAttribute('data-zh-media-still', 'true');

    /* The video's own class, so the still inherits the sizing and the object-fit the
       section gave it and lands in exactly the same box. */
    still.className = video.className;

    /* The name travels with the picture. A slot whose video carries no label is
       decorative, and a decorative still must not start announcing itself. */
    var label = video.getAttribute('aria-label');
    still.setAttribute('alt', label || '');
    if (!label) still.setAttribute('aria-hidden', 'true');

    parent.insertBefore(still, video);
    video.style.display = 'none';
    video.zhMediaStill = still;
  }

  function dropStandIn(video) {
    var still = video && video.zhMediaStill;
    if (!still) return;

    video.zhMediaStill = null;
    video.style.display = '';
    if (still.parentNode && typeof still.parentNode.removeChild === 'function') {
      still.parentNode.removeChild(still);
    }
  }

  /* The button belongs to the video, not the other way round, so the state can be
     pushed from wherever playback actually changed. */
  function syncToggle(video) {
    var button = video.zhMediaToggle;
    if (button) setToggleState(button, !video.paused);
  }

  /* Playback follows the viewport.
   *
   * A page can hold a dozen of these slots, and a dozen videos decoding at once
   * is what makes a page feel broken on a phone. Anything off screen is paused,
   * which also means a slot far down the page never starts at all until it is
   * reached.
   *
   * A video the visitor paused on purpose stays paused: that is what the
   * data-zh-media-held flag records. Without it, scrolling past and back would
   * undo the pause control and with it the WCAG 2.2.2 mechanism. */
  var observer = null;

  /* The observer holds its targets strongly, and the editor replaces a section's
     markup on every change, so the copies it leaves behind would pile up for the
     length of an editing session. Hence the roster and the sweep below, run before
     each new batch is registered.

     isConnected is read defensively: anything that is not explicitly detached stays
     on the roster, so a host without the property simply keeps the old behaviour. */
  var watched = [];

  function sweep() {
    for (var i = watched.length - 1; i >= 0; i--) {
      if (watched[i].isConnected === false) {
        if (observer && typeof observer.unobserve === 'function') observer.unobserve(watched[i]);
        watched.splice(i, 1);
      }
    }
  }

  function watch(scope, video) {
    if (!('IntersectionObserver' in window)) {
      /* No observer means no viewport gate, so the slot counts as permanently on
         screen. It still joins the roster: the retry below is the only thing that
         gets such a slot going after a refused autoplay. */
      scope.zhMediaVisible = true;
      watched.push(scope);
      play(video);
      return;
    }
    if (!observer) {
      /* window-qualified on purpose. A bare global works in a browser but not when
         the asset is evaluated against a constructed scope, which is how the unit
         suite runs it, and the qualified form is the same lookup either way. */
      observer = new window.IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var element = entry.target;
            /* A property rather than an attribute: this changes on every scroll past
               every slot, and none of it is addressable from CSS. */
            element.zhMediaVisible = entry.isIntersecting;
            var media = videoOf(element);
            if (!media) return;
            if (entry.isIntersecting) {
              if (element.getAttribute('data-zh-media-held') !== 'true') play(media);
            } else {
              media.pause();
            }
          });
        },
        { rootMargin: '100px 0px' }
      );
    }
    observer.observe(scope);
    watched.push(scope);
  }

  /* The autoplay retry.
   *
   * A refused play() is silent: the promise rejects, the element stays paused, the
   * poster stays up. It happens more often than the attribute suggests. iOS Low
   * Power Mode refuses every autoplay at the OS level, several mobile browsers
   * refuse until the page has been touched once, and a slow first frame looks the
   * same from here.
   *
   * The observer alone cannot recover from it, because it calls play() only when a
   * slot crosses the viewport edge. A slot that was already on screen when it was
   * refused never gets a second chance, and on a landing page that is most of them:
   * the reader scrolls down, each band is refused once on arrival, and the page
   * settles into a column of stills.
   *
   * Same layer sections/zh-hero.liquid runs for the homepage hero, with the two
   * guards a shared slot needs and a single full-bleed hero does not: a video the
   * visitor paused stays paused, because that is the WCAG 2.2.2 mechanism, and an
   * off-screen slot stays off, because otherwise one tap starts every video on the
   * page at once.
   *
   * Deliberately not `once`, which is where it departs from the hero. The hero has
   * one video and it is on screen at load, so a single retry covers it. A landing
   * page has a video in nearly every band, reaching the viewport over a minute of
   * scrolling, long after a one-shot listener has fired and removed itself. */
  function retryOne(scope, video) {
    if (!scope || !video || !video.paused) return;
    /* A slot that ran to its end is finished, not blocked. Without this every later
       tap would replay a video the visitor has already watched out. */
    if (video.ended) return;
    if (scope.zhMediaVisible !== true) return;
    if (scope.getAttribute('data-zh-media-held') === 'true') return;
    play(video);
  }

  /* On screen, by rect rather than by a second observer.
   *
   * The slots above have the intersection observer to ask; the Impact elements below
   * are not ours to observe and there are few of them. The two callers are a gesture,
   * which is rare, and the frame-coalesced scroll pass, where a layout read is already
   * being paid for.
   *
   * Both axes, because height alone is not visibility here. The product gallery is a
   * horizontal scroll area (snippets/product-gallery.liquid), so every slide shares one
   * row with the visible one and overlaps the viewport vertically however far the
   * visitor has scrolled past it. Measured at 375x812 on /products/zahnpasta-natural-
   * white: the video sat at left 1885 while the first slide was in frame, and the
   * vertical check alone started it. An invisible video costs mobile data and battery,
   * and MediaCarousel._onMediaSettled only pauses it once the visitor swipes on.
   *
   * A display:none element answers an all-zero rect, which fails either half. */
  function onScreen(element) {
    if (!element || typeof element.getBoundingClientRect !== 'function') return false;
    var box = element.getBoundingClientRect();
    var bottomLimit = window.innerHeight || (document.documentElement || {}).clientHeight || 0;
    var rightLimit = window.innerWidth || (document.documentElement || {}).clientWidth || 0;
    return box.bottom > 0 && box.top < bottomLimit && box.right > 0 && box.left < rightLimit;
  }

  /* Impact's own player, brought onto the same behaviour.
   *
   * sections/video, media-with-text, multi-column, media-grid, main-collection and
   * blocks/video keep Impact's <video-media>, which also does YouTube and Vimeo, so
   * they never came through the slot above and never had a retry. Their refusal path
   * is Impact's own: assets/theme.js catches NotAllowedError, sets `suspended` and
   * switches the NATIVE controls on. A background video that was refused once
   * therefore wears a control bar for the rest of the visit, which is a third
   * behaviour again next to the hero's and the slot's.
   *
   * Driven from here rather than patched into theme.js, which is the Impact bundle
   * and is overwritten by the next theme update.
   *
   * Only `[autoplay]`. A <video-media> without it is a player the visitor starts
   * themselves, and there the controls are the entire point.
   *
   * The raw <video> is played rather than the element's own play(), deliberately.
   * That one goes through a Proxy and an `await`, so the call lands in a later
   * microtask and has lost the transient user activation by the time it runs, which
   * is precisely what iOS refuses in Low Power Mode. Keeping Impact in step then takes
   * the one line below: the listeners that set its `playing` attribute hang off a lazy
   * getter, so they have to be woken before the raw play rather than after it. */
  function retryVideoMedia() {
    var nodes = document.querySelectorAll('video-media[autoplay]');

    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];

      /* Impact's record of a deliberate pause, set by the control it injects (see
         _setupPauseToggle in assets/theme.js). Honoured for the same reason
         data-zh-media-held is above: it is the WCAG 2.2.2 mechanism. */
      if (node._pausedByVisitor === true) continue;

      /* A YouTube or Vimeo slot, which this pass has no business touching: there is no
         <video> to play, and waking Impact's player further down would inject the
         iframe and pull the provider's API script in on a slot nobody asked to start.
         Read from the attribute rather than inferred from the missing <video>: that
         those slots hold a <template> with an iframe is a property of six Liquid files,
         not something this loop can promise itself. */
      if (node.hasAttribute('host')) continue;

      var media = videoOf(node);
      if (!media || !media.paused || media.ended) continue;
      if (!onScreen(node)) continue;

      /* The bar Impact put up comes down again, so a background video that recovers
         does not keep furniture it never had. Refused again, Impact puts both back,
         which is the honest state: still blocked, still offered. */
      if (node.hasAttribute('suspended')) {
        node.removeAttribute('suspended');
        media.controls = false;
      }

      /* Wake Impact's player, or it never hears the start below.
       *
       * <video-media> keeps its state in a `playing` attribute, and the listeners that
       * set it sit on the <video>, attached by the element's `player` getter. That
       * getter is lazy: nothing attaches them until Impact itself plays or pauses. So
       * a raw play() beforehand lands with nobody listening, the attribute is never
       * set, and no later call repairs it, because play() on a running video fires no
       * event at all.
       *
       * The pause control theme.js injects reads that attribute. Without this it shows
       * the play glyph over a running video and every click takes the play() branch,
       * which does nothing: an autoplaying video with no way to stop it, which is the
       * WCAG 2.2.2 mechanism gone.
       *
       * A bare statement, the way ModelMedia.connectedCallback wakes the same getter
       * in assets/theme.js. It stays below the guards above, the host one in
       * particular: on a YouTube or Vimeo slot this getter injects the iframe and
       * pulls the provider's API script in.
       *
       * Guarded, because it is the one place in this file that reaches into
       * assets/theme.js, and that file is the Impact bundle: the next theme update
       * replaces it wholesale. This loop walks every video on the page, so one element
       * that throws must not cost all the others their retry. The play below still
       * runs; only the state sync is lost, which is the state the rest of this comment
       * describes. */
      try {
        node.player;
      } catch (error) {
        /* Deliberately swallowed, see above. Nothing here can act on it. */
      }

      play(media);
    }
  }

  function retryAll() {
    for (var i = 0; i < watched.length; i++) {
      retryOne(watched[i], videoOf(watched[i]));
    }
    retryVideoMedia();
  }

  /* Swap-only mode.
   *
   * For the Impact sections that bring their own player: sections/video,
   * media-grid, media-with-text, multi-column, main-collection and blocks/video
   * wrap a video_tag in <video-media>, which has its own autoplay handling and
   * also supports a YouTube or Vimeo embed instead. Those are not rebuilt on
   * zh-media, they only needed the one thing markup cannot do: a different file
   * below the breakpoint. <video> ignores the media attribute on a <source>, so
   * this is the only way.
   *
   * Hence a second, deliberately dumb hook. It swaps the file and touches nothing
   * else: no intersection observer, no muted handling, no pause control, so it
   * cannot fight the element's own logic. */
  function initSwaps(root) {
    var scopes = (root || document).querySelectorAll('[data-zh-media-swap]');

    Array.prototype.forEach.call(scopes, function (scope) {
      if (scope.dataset[INITIALISED] === 'true') return;
      var video = videoOf(scope);
      if (!video) return;
      scope.dataset[INITIALISED] = 'true';
      applySource(scope, video);
    });
  }

  /* The pause control inside a linked card.
   *
   * Nine slots in the theme sit inside an <a>: category tiles, collection list,
   * image link blocks, product cards, the magazine badges, the product track, the
   * mega menu promo. A <button> in an <a> is interactive content nested in
   * interactive content, which is invalid HTML and an axe-core nested-interactive
   * violation, and it leaves a screen reader announcing a button inside a link.
   *
   * Deleting the control is not an option: it is the WCAG 2.2.2 mechanism for a
   * looping autoplay video. So it is moved out instead, to just after the anchor.
   * It is invisible until focused anyway, so where exactly it sits does not show,
   * and the tab order stays sensible: card, then its pause control.
   *
   * Done here and not in Liquid because the snippet cannot see whether its caller
   * wrapped it in a link, and tagging nine call sites by hand is nine chances to
   * miss one, plus a tenth the next time someone adds a card.
   *
   * The video reference travels with the button, since moving it out of the
   * wrapper also moves it out of the scope the click handler would search.
   *
   * The move has a price the CSS has to pay. The control is positioned against
   * .zh-media, and that box stays behind inside the link, so the offsets it carries
   * would resolve against whatever ancestor happens to be positioned, or against the
   * initial containing block: on focus the control appears nowhere near its card, and
   * on a long page often outside the viewport entirely. Hence the marker. The CSS
   * gives a marked control no offsets at all and lets it come back as a fixed overlay
   * once it is focused, which is a place the visitor can actually see.
   *
   * Putting it back over the card would mean restructuring the nine call sites to the
   * link-overlay pattern: the card stops being the <a> and becomes a positioned box
   * that the link covers with a pseudo-element. Only then can a control sit over the
   * media and outside the link at the same time. */
  function unnestToggle(scope, video) {
    var toggle = scope.querySelector('[data-zh-media-toggle]');
    if (!toggle) return null;

    toggle.zhMediaVideo = video;

    var anchor = scope.closest ? scope.closest('a') : null;
    if (anchor && anchor.parentNode && typeof anchor.insertAdjacentElement === 'function') {
      anchor.insertAdjacentElement('afterend', toggle);
      toggle.setAttribute('data-zh-media-unnested', 'true');
    }

    return toggle;
  }

  function init(root) {
    initSwaps(root);
    sweep();

    var scopes = (root || document).querySelectorAll('[data-zh-media]');

    Array.prototype.forEach.call(scopes, function (scope) {
      if (scope.dataset[INITIALISED] === 'true') return;
      var video = videoOf(scope);
      if (!video) return;
      scope.dataset[INITIALISED] = 'true';

      applySource(scope, video);

      var toggle = unnestToggle(scope, video);
      /* The snippet marks an autoplay slot with data-zh-media-autoplay instead of the
         attribute, which would have the browser fetch the file at parse time (see the
         comment at the <video> in snippets/zh-media.liquid). `autoplay` is still read
         for markup cached from before. */
      var autoplays = video.hasAttribute('data-zh-media-autoplay') || video.hasAttribute('autoplay');

      /* Muted has to be set as a property too. Safari reads the property, not
         the attribute, when it decides whether autoplay is allowed. */
      if (autoplays) {
        video.muted = true;
        video.defaultMuted = true;
        watch(scope, video);

        /* The "not decoded yet" half of a refusal. Bound per element rather than on
           the document, since these events do not bubble. Not `once`: a source swap
           at the breakpoint calls load() and the element runs through them again. */
        ['loadeddata', 'canplay'].forEach(function (type) {
          video.addEventListener(type, function () {
            retryOne(scope, video);
          });
        });

        /* `playing` rather than `play`: play fires on the attempt, playing on the
           first frame. Taking the still down on the attempt would show the empty
           video for as long as the buffer needs. */
        video.addEventListener('playing', function () {
          dropStandIn(video);
        });
      }

      if (toggle) {
        /* The back reference, so a refused play() can correct the button from
           inside play() without carrying the element around. */
        video.zhMediaToggle = toggle;

        /* Seeded from the video, not from the autoplay attribute.
         *
         * The attribute says what was asked for, `paused` says what happened. Those
         * two part company more often than one would think: a slot below the fold
         * has not been played yet, and in Low Power Mode it never will be. Seeding
         * from the attribute labelled every one of those "pause", so the control
         * offered to stop a video that was standing still, and a visitor who pressed
         * it got the opposite of what the icon promised. */
        setToggleState(toggle, !video.paused);

        video.addEventListener('play', function () {
          setToggleState(toggle, true);
        });
        video.addEventListener('pause', function () {
          setToggleState(toggle, false);
        });
      }
    });
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest ? event.target.closest('[data-zh-media-toggle]') : null;
    if (!toggle) return;

    /* The reference first, the DOM walk second: a control that was moved out of a
       linked card no longer has the wrapper as an ancestor. */
    var scope = toggle.closest('[data-zh-media]');
    var video = toggle.zhMediaVideo || (scope ? videoOf(scope) : null);
    if (!video) return;

    /* The held flag lives on the wrapper, so it is resolved from the video when the
       control has been moved away from it. */
    var holder = scope || (video.closest ? video.closest('[data-zh-media]') : null);

    if (video.paused) {
      if (holder) holder.setAttribute('data-zh-media-held', 'false');
      play(video);
    } else {
      if (holder) holder.setAttribute('data-zh-media-held', 'true');
      video.pause();
    }
    setToggleState(toggle, !video.paused);
  });

  /* One listener for every slot on the page, hence the query on resize rather
     than a matchMedia listener per element.

     Coalesced to one pass per frame. resize fires per pixel while a window is
     dragged, and on a phone for every show and hide of the browser bar, so an
     unthrottled document-wide query here is paid on exactly the device that can
     least afford it. A frame is the right grain: nothing below it can be seen. */
  var resizeQueued = false;

  function refreshSources() {
    resizeQueued = false;
    var scopes = document.querySelectorAll('[data-zh-media-desktop]');
    Array.prototype.forEach.call(scopes, function (scope) {
      var video = videoOf(scope);
      if (video) applySource(scope, video);
    });
  }

  window.addEventListener('resize', function () {
    if (resizeQueued) return;
    resizeQueued = true;
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(refreshSources);
    } else {
      window.setTimeout(refreshSources, 100);
    }
  });

  /* Run straight out of the handler, not queued into a frame.
   *
   * These are the events that give a document user activation in WebKit, and the
   * activation is transient: a play() call from a later task has lost it, which is
   * exactly the case Low Power Mode refuses. They also fire rarely enough that a
   * walk over the roster costs nothing worth measuring. */
  ['pointerdown', 'touchstart', 'keydown'].forEach(function (type) {
    window.addEventListener(type, retryAll, { passive: true });
  });

  /* Scroll grants no activation, so this one is for the ordinary "was not ready
     yet" refusal rather than for Low Power Mode. It fires per frame, so it is
     coalesced the way the resize handler above is. */
  var retryQueued = false;

  function retryPass() {
    retryQueued = false;
    retryAll();
  }

  window.addEventListener(
    'scroll',
    function () {
      if (retryQueued) return;
      retryQueued = true;
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(retryPass);
      } else {
        window.setTimeout(retryPass, 100);
      }
    },
    { passive: true }
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  /* The editor replaces a section's markup wholesale, so the fresh copy needs
     initialising. The guard flag lives on the element and goes away with it. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  /* The product page replaces its own markup too.
   *
   * Switching product on a combined listing takes the `productChange` path in
   * theme.js, which does replaceWith() on the whole <product-rerender> subtree and
   * dispatches no shopify:section:load. A video in the product's image block would
   * come back without any wiring: nothing starts it, and its pause button would be
   * dead, which takes the WCAG 2.2.2 mechanism with it.
   *
   * Caught here rather than patched into theme.js, which is the Impact bundle and
   * gets overwritten by the next theme update.
   *
   * Two details make this work. The capture phase, because product:rerender is
   * dispatched on the form WITHOUT bubbles, and a non-bubbling event still travels
   * the capture path from the root down to its target. And the timeout, because
   * capture runs BEFORE the listener that swaps the DOM: by the next task the new
   * markup is in place. init() is idempotent, so a spurious run costs one
   * querySelectorAll. */
  document.addEventListener(
    'product:rerender',
    function () {
      window.setTimeout(function () {
        init();
      }, 0);
    },
    true
  );

  window.zhMediaInit = init;
})();
